import { $applyNodeReplacement, DecoratorNode, type LexicalNode, type NodeKey, type SerializedLexicalNode, type Spread } from 'lexical'
import { type ReactElement } from 'react'
import classNames from 'classnames'
import styles from '../EmailEditor.module.scss'
import { type Badge } from '../emailTemplateTypes'
import { LockedBlockView } from '../LockedBlockView'
import { optionalBadges, requireString } from './serializedKeys'

export type SerializedLockedBlockNode = Spread<{ blockId: string; label: string; html: string; badges?: Badge[] }, SerializedLexicalNode>

const NODE = 'email-locked'

const isInvisible = (html: string): boolean =>
  html
    .replace(/\{%-?[\s\S]*?-?%\}/g, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .trim() === ''

export class LockedBlockNode extends DecoratorNode<ReactElement> {
  __blockId: string
  __label: string
  __html: string
  __badges: Badge[]

  static getType(): string {
    return NODE
  }

  static clone(node: LockedBlockNode): LockedBlockNode {
    return new LockedBlockNode(node.__blockId, node.__label, node.__html, node.__badges, node.__key)
  }

  constructor(blockId: string, label: string, html: string, badges: Badge[], key?: NodeKey) {
    super(key)
    this.__blockId = blockId
    this.__label = label
    this.__html = html
    this.__badges = badges
  }

  static importJSON(json: SerializedLockedBlockNode): LockedBlockNode {
    const raw = json as unknown as Record<string, unknown>
    return $createLockedBlockNode(
      requireString(raw, NODE, 'blockId'),
      requireString(raw, NODE, 'label'),
      requireString(raw, NODE, 'html'),
      optionalBadges(raw, NODE) ?? [],
    ).updateFromJSON(json)
  }

  exportJSON(): SerializedLockedBlockNode {
    return {
      ...super.exportJSON(),
      blockId: this.__blockId,
      label: this.__label,
      html: this.__html,
      ...(this.__badges.length ? { badges: this.__badges } : {}),
    }
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('div')
    dom.className = classNames(styles.locked, { [styles.lockedHidden]: isInvisible(this.__html) })
    dom.dataset.block = this.__blockId
    if (this.__badges.length) {
      dom.dataset.badge = ''
    }
    return dom
  }

  updateDOM(): false {
    return false
  }

  getBlockId(): string {
    return this.getLatest().__blockId
  }

  getLabel(): string {
    return this.getLatest().__label
  }

  getHtml(): string {
    return this.getLatest().__html
  }

  getBadges(): Badge[] {
    return this.getLatest().__badges
  }

  isInline(): false {
    return false
  }

  isKeyboardSelectable(): false {
    return false
  }

  decorate(): ReactElement {
    const parent = this.getParent()
    const inLockedGroup = parent?.getType() === 'email-group' && parent.getChildren().every((child) => child.getType() === NODE)
    return <LockedBlockView html={this.__html} label={this.__label} badges={this.__badges} showLockTag={!inLockedGroup} />
  }
}

export const $createLockedBlockNode = (blockId: string, label: string, html: string, badges: Badge[]): LockedBlockNode =>
  $applyNodeReplacement(new LockedBlockNode(blockId, label, html, badges))

export const $isLockedBlockNode = (node: LexicalNode | null | undefined): node is LockedBlockNode => node instanceof LockedBlockNode
